export interface HtmlBlockItem {
  html: string
  height: number
}

type HtmlBlockInput = Partial<Omit<HtmlBlockItem, 'height'>> & {
  height?: unknown
}

export type HtmlBlockMarkdownSegment =
  | {
      type: 'markdown'
      markdown: string
    }
  | {
      type: 'htmlBlock'
      htmlBlock: HtmlBlockItem
    }

const DEFAULT_HTML_BLOCK_HEIGHT = 240
const MIN_HTML_BLOCK_HEIGHT = 80
const MAX_HTML_BLOCK_HEIGHT = 1200

const HTML_BLOCK_PATTERN = /\[\[html\b([^\]]*)\]\]/g
const HTML_BLOCK_HTML_PATTERN =
  /<html-embed-block\b([^>]*)>\s*<\/html-embed-block>|<html-embed-block\b([^>]*)\/>/g
const HTML_BLOCK_PLACEHOLDER = 'KARAKURI_HTML_BLOCK'

export const createDefaultHtmlBlockItem = (): HtmlBlockItem => ({
  html: '',
  height: DEFAULT_HTML_BLOCK_HEIGHT,
})

export function normalizeHtmlBlockItem(item: HtmlBlockInput): HtmlBlockItem {
  return {
    html: typeof item.html === 'string' ? item.html : '',
    height: normalizeHeight(item.height),
  }
}

export function encodeHtmlBlockItem(item: HtmlBlockInput): string {
  return encodeURIComponent(JSON.stringify(normalizeHtmlBlockItem(item)))
}

export function decodeHtmlBlockItem(encodedItem: string | undefined): HtmlBlockItem {
  if (!encodedItem) {
    return createDefaultHtmlBlockItem()
  }

  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(encodedItem))
    if (!isHtmlBlockItemLike(parsed)) {
      return createDefaultHtmlBlockItem()
    }

    return normalizeHtmlBlockItem(parsed)
  } catch {
    return createDefaultHtmlBlockItem()
  }
}

export function serializeHtmlBlockMarkdown(item: HtmlBlockInput): string {
  return `[[html data="${escapeShortcodeAttribute(encodeHtmlBlockItem(item))}"]]`
}

export function splitHtmlBlocksFromMarkdown(markdown: string): HtmlBlockMarkdownSegment[] {
  const segments: HtmlBlockMarkdownSegment[] = []
  let lastIndex = 0

  markdown.replace(
    HTML_BLOCK_PATTERN,
    (matchedText: string, attributes: string, offset: number) => {
      if (offset > lastIndex) {
        segments.push({ type: 'markdown', markdown: markdown.slice(lastIndex, offset) })
      }
      segments.push({ type: 'htmlBlock', htmlBlock: parseHtmlBlockAttributes(attributes) })
      lastIndex = offset + matchedText.length
      return matchedText
    }
  )

  if (lastIndex < markdown.length) {
    segments.push({ type: 'markdown', markdown: markdown.slice(lastIndex) })
  }

  return segments.length > 0 ? segments : [{ type: 'markdown', markdown }]
}

export function replaceHtmlBlockMarkdownWithPlaceholders(markdown: string): {
  markdown: string
  htmlBlocks: HtmlBlockItem[]
} {
  const htmlBlocks: HtmlBlockItem[] = []
  const replacedMarkdown = markdown.replace(
    HTML_BLOCK_PATTERN,
    (_matchedText, attributes: string) => {
      const index = htmlBlocks.length
      htmlBlocks.push(parseHtmlBlockAttributes(attributes))
      return `${HTML_BLOCK_PLACEHOLDER}_${index}`
    }
  )

  return { markdown: replacedMarkdown, htmlBlocks }
}

export function restoreHtmlBlockHtmlPlaceholders(
  html: string,
  htmlBlocks: HtmlBlockItem[]
): string {
  return htmlBlocks.reduce((restoredHtml, htmlBlock, index) => {
    const placeholder = `${HTML_BLOCK_PLACEHOLDER}_${index}`
    const htmlBlockHtml = createHtmlBlockHtml(htmlBlock)
    return restoredHtml
      .replace(new RegExp(`<p>\\s*${placeholder}\\s*</p>`, 'g'), htmlBlockHtml)
      .replace(new RegExp(placeholder, 'g'), htmlBlockHtml)
  }, html)
}

export function replaceHtmlBlockHtmlWithPlaceholders(html: string): {
  html: string
  htmlBlocks: HtmlBlockItem[]
} {
  const htmlBlocks: HtmlBlockItem[] = []
  const replacedHtml = html.replace(
    HTML_BLOCK_HTML_PATTERN,
    (_matchedText, attributesA: string | undefined, attributesB: string | undefined) => {
      const index = htmlBlocks.length
      htmlBlocks.push(decodeHtmlBlockItem(readHtmlAttribute(attributesA ?? attributesB ?? '')))
      return `<p>${HTML_BLOCK_PLACEHOLDER}_${index}</p>`
    }
  )

  return { html: replacedHtml, htmlBlocks }
}

export function restoreHtmlBlockMarkdownPlaceholders(
  markdown: string,
  htmlBlocks: HtmlBlockItem[]
): string {
  return htmlBlocks.reduce(
    (restoredMarkdown, htmlBlock, index) =>
      restoredMarkdown.replace(
        createMarkdownPlaceholderRegExp(`${HTML_BLOCK_PLACEHOLDER}_${index}`),
        serializeHtmlBlockMarkdown(htmlBlock)
      ),
    markdown
  )
}

function parseHtmlBlockAttributes(attributes: string): HtmlBlockItem {
  return decodeHtmlBlockItem(readShortcodeAttribute(attributes, 'data'))
}

function createHtmlBlockHtml(item: HtmlBlockItem): string {
  return `<html-embed-block data-html="${escapeHtmlAttribute(
    encodeHtmlBlockItem(item)
  )}"></html-embed-block>`
}

function readShortcodeAttribute(attributes: string, name: string): string | undefined {
  const pattern = new RegExp(`${name}="((?:\\\\.|[^"\\\\])*)"`)
  const matched = pattern.exec(attributes)
  return matched ? unescapeShortcodeAttribute(matched[1]) : undefined
}

function readHtmlAttribute(attributes: string): string | undefined {
  const matched = /data-html=(?:"([^"]*)"|'([^']*)'|([^\s>]+))/.exec(attributes)
  return matched?.[1] ?? matched?.[2] ?? matched?.[3]
}

function escapeShortcodeAttribute(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function unescapeShortcodeAttribute(value: string): string {
  return value.replace(/\\"/g, '"').replace(/\\\\/g, '\\')
}

function escapeHtmlAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;')
}

function createMarkdownPlaceholderRegExp(placeholder: string): RegExp {
  return new RegExp(escapeRegExp(placeholder).replace(/_/g, '\\\\?_'), 'g')
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function normalizeHeight(value: unknown): number {
  const height = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(height)) {
    return DEFAULT_HTML_BLOCK_HEIGHT
  }

  return Math.min(MAX_HTML_BLOCK_HEIGHT, Math.max(MIN_HTML_BLOCK_HEIGHT, Math.round(height)))
}

function isHtmlBlockItemLike(value: unknown): value is HtmlBlockInput {
  return typeof value === 'object' && value !== null
}
